import React, { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { FormikProvider, useFormik } from 'formik';
import { useTranslation } from 'next-i18next';
import { dateIsValid } from '@helpers/utils/date-utils';
import { usePreferences } from '@context/preferences.context';
import styles from '../filters.module.scss';
import DateSelect from './date-select';

const formInitValue = {
  day: '',
  month: '',
  year: '',
};

export type DateType = typeof formInitValue;

interface DateFilterProps {
  onDateChange: ({ fromDate, toDate }: { fromDate: string; toDate: string }) => void;
}

const DateFilter: React.FC<DateFilterProps> = ({ onDateChange }) => {
  const { t } = useTranslation('orders-sales');

  const { language } = usePreferences();

  const [lastDayOfMonth, setLastDayOfMonth] = useState<number>();

  const isDateValid = useCallback((day: number, month: number, year: number) => {
    return dateIsValid(day, month, year);
  }, []);

  const formik = useFormik({
    initialValues: formInitValue,
    onSubmit: () => {},
  });

  const { values, setFieldValue } = formik;

  useEffect(() => {
    const { year, month, day } = values;

    let fromDate = '';
    let toDate = '';

    // Select one date
    if (year && month && day) {
      fromDate = `${year}-${month}-${day}`;

      if (!isDateValid(Number(day), Number(month), Number(year))) {
        setFieldValue('day', '');
      }

      const lastDate = new Date(Number(year), Number(month), 0).getDate();
      setLastDayOfMonth(lastDate);
    }
    // Select start to end of specific month
    else if (year && month) {
      const lastDate = new Date(Number(year), Number(month), 0).getDate();
      fromDate = `${year}-${month}-01`;
      toDate = `${year}-${month}-${lastDate}`;
      setLastDayOfMonth(lastDate);
    }
    // Select start to end of specific year
    else if (year) {
      const lastDate = new Date(Number(year), 12, 0);
      fromDate = `${year}-01-01`;
      toDate = `${year}-12-${lastDate.getDate()}`;
    }
    // Empty
    else {
      return;
    }

    onDateChange({ fromDate, toDate });
  }, [values, isDateValid, setFieldValue]);

  const getYearOptions = () => {
    const years = [];
    const date = new Date();
    const thisYear = date.getFullYear();
    const tenYearsBack = thisYear - 10;

    for (let year = thisYear; year > tenYearsBack; year--) {
      years.push(year);
    }

    return years;
  };

  const getMonthName = (yearNumber: number, monthNumber: number, locale?: string): string => {
    const date = new Date(yearNumber, monthNumber);
    const dateLocale = (locale === 'hk' ? 'zh-HK' : locale) || 'default';

    return date.toLocaleString(dateLocale, { month: 'long' });
  };

  return (
    <FormikProvider value={formik}>
      <form>
        <div className={styles.formGroup}>
          <div className={clsx(styles['vc-formUnit'], styles.dates)}>
            <DateSelect
              name="year"
              value={values.year}
              placeholder={t('MY_ACCOUNT.ORDERS_SALES.LIST.SEARCH.YEAR')}
              options={getYearOptions().map((i: number) => ({ value: i, label: i }))}
            />

            <DateSelect
              name="month"
              value={values.month}
              disabled={!values.year}
              placeholder={t('MY_ACCOUNT.ORDERS_SALES.LIST.SEARCH.MONTH')}
              options={Array.from(Array(12).keys()).map((i: number) => ({
                value: (i + 1).toString().padStart(2, '0'),
                label: getMonthName(Number(values.year || 0), i, language),
              }))}
            />

            <DateSelect
              name="day"
              value={values.day}
              disabled={!lastDayOfMonth}
              placeholder={t('MY_ACCOUNT.ORDERS_SALES.LIST.SEARCH.DAY')}
              options={Array.from(Array(lastDayOfMonth).keys()).map((i: number) => ({
                value: (i + 1).toString().padStart(2, '0'),
                label: i + 1,
              }))}
            />
          </div>
        </div>
      </form>
    </FormikProvider>
  );
};

export default DateFilter;
